export type FieldSelectionTooltipSection = {
	text: string;
	color: string;
};

export type FieldSelectionTooltipInfo = {
	title: string;
	sections: readonly FieldSelectionTooltipSection[];
};

export const FieldSelectionForInvoicesInfoStrings = {
	required: {
		title: "Make Explicitly Required",
		sections: [
			{ text: "If checked, increasingly more advanced methods for extraction will be applied until a value is determined.", color: "#ff4c9c" },
			{ text: "Don't check for fields that might not exist in all of your invoices.", color: "#ff6680" },
		],
	},
	validation_source: {
		title: "Attached Data",
		sections: [
			{ text: "Attach data to the process and define how it should be used.", color: "#ff4c9c" },
		],
	},
	is_key: {
		title: "Filter the Attached Data",
		sections: [
			{ text: "If checked, we'll take the value of the invoice field and use it to filter the attached data. Then all other fields have filtered data to reconcile against.", color: "#ff4c9c" },
			{ text: "Field Id should be the same as the invoice field id.", color: "#ff6680" },
		],
	},
	allowed_deviation: {
		title: "Allowed Deviation",
		sections: [
			{ text: "What difference is acceptable between recognized values and kognify's recalculation results?", color: "#ff4c9c" },
			{ text: "Changes to this setting apply for newly processed invoices.", color: "#ff6680" },
		],
	},
	preferred_currency: {
		title: "Preferred Currency",
		sections: [
			{ text: "Select the preferred currency for invoice processing.", color: "#ff4c9c" },
			{ text: "This setting applies to newly processed invoices.", color: "#ff6680" },
		],
	},
	hardcode_language: {
		title: "Indicative Languages",
		sections: [
			{ text: "Languages that the assistant should expect when handling the content.", color: "#ff4c9c" },
		],
	},
	use_v4_extraction: {
		title: "V4 Base Processing",
		sections: [
			{ text: "Newest version of kognify's root document interfacing solution.", color: "#ff4c9c" },
			{ text: "Base Processing converts raw document data into ordered data in a multi-step agentic process.", color: "#ff6680" },
			{ text: "V4 introduces better agentic decision making and more available skills leading to better results.", color: "#ff8a68" }
		],
	},
	use_latest_model: {
		title: "Latest Model",
		sections: [
			{ text: "Use latest ai model for some of the tasks.", color: "#ff4c9c" },
			{ text: "Warning: For testing only. This will enable models that we are still in the process of evaluation and testing.", color: "#ff6680" },
		],
	},
	adv_post_check: {
		title: "Intelligent Recheck",
		sections: [
			{ text: "After each invoice is processed, an intelligent recheck reviews the full result like a human reviewing the overview to catch issues.", color: "#ff4c9c" },
			{ text: "Improves overall success rate.", color: "#ff6680" },
		],
	},
	inv_pre_agent: {
		title: "File-to-Meaning Reconstruction",
		sections: [
			{ text: "Agentic Reconstruction V2 is leaps and bounds ahead of previous versions, managing to preserve the content, layout, meaning and intent of a document correctly.", color: "#ff4c9c" },
			{ text: "It provides better verifiability of its results by providing step-by-step reasoning & justification for the transformation process results.", color: "#ff6680" },
			{ text: "This makes it a solid bedrock supporting subsequent advanced analysis and decision-making on top of it.", color: "#ff8a68" },
		],
	},
	enable_custom_props: {
		title: "Custom Properties",
		sections: [
			{ text: "Allow creation of custom properties for both invoice handling & per line item handling.", color: "#ff4c9c" },
			{ text: "Custom properties allow you to define additional required data. It can fulfill specific needs but also it can determine sophisticated insights, data representations, decisions and more.", color: "#ff6680" },
		],
	},
	enable_expanded_extraction: {
		title: "Expanded Data Collection",
		sections: [
			{ text: "Get more data from the invoice, which might be useful for advanced matching and verification configurations.", color: "#ff4c9c" },
		],
	},
	extended_cli_code: {
		title: "Extended Client Code",
		sections: [
			{ text: "Enable extended client and supplier determination if invoices with the Client Item codes are available.", color: "#ff4c9c" },
		],
	},
	enable_approvals: {
		title: "Enable Approvals",
		sections: [
			{ text: "Enable human approval for processed invoices.", color: "#ff4c9c" },
		],
	},
	po_editing_enabled: {
		title: "Enable PO Match Change",
		sections: [
			{ text: "Allow changing the matched PO on invoices when reviewing results.", color: "#ff4c9c" },
		],
	},
	po_delivery_tracking_on: {
		title: "Track PO Delivery",
		sections: [
			{ text: "On invoice approval, subtract the invoiced quantity from the remaining tracked quantity of each linked PO line item.", color: "#ff4c9c" },
		],
	},
	allow_linked_data_editing: {
		title: "Allow linked data editing",
		sections: [
			{ text: "Let users add, edit and delete rows of the linked PO source data directly from the lists sidebar. Changes are written back to the linked data.", color: "#ff4c9c" },
		],
	},
	do_inv_item_combining: {
		title: "Combine Invoice Items",
		sections: [
			{ text: "Combine matching invoice line items during PO matching.", color: "#ff4c9c" },
		],
	},
	split_files_per_invoice: {
		title: "Split Files Per Invoice",
		sections: [
			{ text: "Multi Invoice File Support.", color: "#ff4c9c" },
			{ text: "Enable splitting file into multiple files, 1 per invoice. Useful in case of multi-invoice files.", color: "#ff6680" },
		],
	},
	align_rotation: {
		title: "Auto Page Orientation",
		sections: [
			{ text: "Automatically correct page orientation. Useful for scanned documents and images where the document orientation might be incorrect.", color: "#ff4c9c" },
			{ text: "Rotate sideways pages.", color: "#ff6680" },
		],
	},
	scoped_rules: {
		title: "Scoped Rules",
		sections: [
			{ text: "Manage supplier-scoped invoice rules.", color: "#ff4c9c" },
			{ text: "Per-supplier pre & post processing rules for unconventional cases like handwritten, incomplete, and other special cases.", color: "#ff6680" },
		],
	},
	export_main: {
		title: "Export Main Fields",
		sections: [
			{ text: "Export main field config to clipboard.", color: "#ff4c9c" },
		],
	},
	import_main: {
		title: "Import Main Fields",
		sections: [
			{ text: "Import main field config from config export.", color: "#ff4c9c" },
		],
	},
	export_item: {
		title: "Export Item Fields",
		sections: [
			{ text: "Export item field config to clipboard.", color: "#ff4c9c" },
		],
	},
	import_item: {
		title: "Import Item Fields",
		sections: [
			{ text: "Import item field config from config export.", color: "#ff4c9c" },
		],
	},
} as const satisfies Record<string, FieldSelectionTooltipInfo>;
